// ═══════════════════════════════════════════════════════════
// Redactions API
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { createAuditEntry } from './auditLog';

export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface Redaction {
  recordId:      number;
  documentId:    number;
  requestId?:    number;
  page:          number;
  coordinates:   { x: number; y: number; width: number; height: number };
  exemptionCode: string;
  exemptionDesc: string;
  redactedBy:    string;
  redactedAt:    string;
  reviewedBy?:   string;
  reviewedAt?:   string;
  reviewStatus:  ReviewStatus;
  notes?:        string;
}

export interface CreateRedactionInput {
  documentId:    number;
  requestId?:    number;
  page:          number;
  coordinates:   { x: number; y: number; width: number; height: number };
  exemptionCode: string;
  exemptionDesc: string;
  redactedByEmail: string;
  notes?: string;
}

const F = FIELDS.redactions;

function rowToRedaction(row: Record<string, unknown>): Redaction {
  let coordinates = { x: 0, y: 0, width: 0, height: 0 };
  try { coordinates = JSON.parse(String(row[F.coordinates] ?? '{}')); } catch { /* ignore */ }
  return {
    recordId:      row['record_id'] as number,
    documentId:    Number(row[F.documentId] ?? 0),
    requestId:     row[F.requestId] as number | undefined,
    page:          Number(row[F.page] ?? 1),
    coordinates,
    exemptionCode: String(row[F.exemptionCode] ?? ''),
    exemptionDesc: String(row[F.exemptionDesc] ?? ''),
    redactedBy:    String(row[F.redactedBy] ?? ''),
    redactedAt:    String(row[F.redactedAt] ?? ''),
    reviewedBy:    row[F.reviewedBy] as string | undefined,
    reviewedAt:    row[F.reviewedAt] as string | undefined,
    reviewStatus:  (row[F.reviewStatus] as ReviewStatus) ?? 'pending',
    notes:         row[F.notes] as string | undefined,
  };
}

export async function getRedactionsForDocument(documentId: number): Promise<Redaction[]> {
  const { records } = await queryRecords(TABLES.redactions, {
    filters: [{ field: F.documentId, operator: 'eq', value: documentId }],
    limit: 500,
  });
  return (records as Record<string, unknown>[]).map(rowToRedaction);
}

export async function getRedactionsForRequest(requestId: number): Promise<Redaction[]> {
  const { records } = await queryRecords(TABLES.redactions, {
    filters: [{ field: F.requestId, operator: 'eq', value: requestId }],
    limit: 500,
  });
  return (records as Record<string, unknown>[]).map(rowToRedaction);
}

export async function createRedaction(input: CreateRedactionInput): Promise<Redaction> {
  const now = new Date().toISOString();
  const row = await createRecord(TABLES.redactions, {
    [F.documentId]:   input.documentId,
    [F.requestId]:    input.requestId ?? null,
    [F.page]:         input.page,
    [F.coordinates]:  JSON.stringify(input.coordinates),
    [F.exemptionCode]:input.exemptionCode,
    [F.exemptionDesc]:input.exemptionDesc,
    [F.redactedBy]:   input.redactedByEmail,
    [F.redactedAt]:   now,
    [F.reviewStatus]: 'pending',
    [F.notes]:        input.notes ?? '',
  });

  if (input.requestId) {
    await createAuditEntry({
      requestId: input.requestId,
      userEmail:  input.redactedByEmail,
      action:    'redaction.created',
      details:   JSON.stringify({ documentId: input.documentId, page: input.page, code: input.exemptionCode }),
    });
  }

  return rowToRedaction(row as Record<string, unknown>);
}

export async function reviewRedaction(
  recordId: number,
  status: 'approved' | 'rejected',
  reviewerEmail: string,
  requestId?: number,
): Promise<Redaction> {
  const now = new Date().toISOString();
  const row = await updateRecord(TABLES.redactions, recordId, {
    [F.reviewStatus]: status,
    [F.reviewedBy]:   reviewerEmail,
    [F.reviewedAt]:   now,
  });

  if (requestId) {
    await createAuditEntry({
      requestId,
      userEmail:  reviewerEmail,
      action:    status === 'approved' ? 'redaction.approved' : 'redaction.rejected',
      details:   JSON.stringify({ redactionId: recordId }),
    });
  }

  return rowToRedaction(row as Record<string, unknown>);
}

export async function deleteRedaction(recordId: number): Promise<void> {
  // GAB doesn't have a delete endpoint in the standard API wrapper.
  // Mark as rejected with a special note instead.
  await updateRecord(TABLES.redactions, recordId, {
    [F.reviewStatus]: 'rejected',
    [F.notes]:        'Deleted by staff',
  });
}
